import type { Dispatch } from '@reduxjs/toolkit'
import { MedChronEventsActions } from '@/medical-chronology/events/store/slice'
import { MedChronReferencesActions } from '@/medical-chronology/references/store/slice'
import { getMedChronAssetByMatter } from '../fetch/get-med-chron-asset-by-matter'
import { getMedChronAssetEvents } from '../fetch/get-med-chron-asset-events'
import { getMedChronAssetFiles } from '../fetch/get-med-chron-asset-files'
import { MedChronAssetActions, type MedChronAssetState } from './slice'

type WorkspaceLoader = {
  dispatch: Dispatch
  getState: () => { medChronAssetState: MedChronAssetState }
  isCurrent?: () => boolean
}

/** Shared by page entry and lease recovery; force bypasses the loaded chronology version. */
export async function loadMedChronWorkspace(
  { matterId, overviewOnly, force }: { matterId: string; overviewOnly?: boolean; force?: boolean },
  { dispatch, getState, isCurrent = () => true }: WorkspaceLoader
) {
  // A full page load must still run when the sidebar is already loading only the overview.
  if (!isCurrent() || (getState().medChronAssetState.assetLoadingByMatterId[matterId] && overviewOnly)) return
  dispatch(MedChronAssetActions.setAssetLoading({ matterId, isLoading: true }))
  dispatch(MedChronAssetActions.setAssetError({ matterId, hasError: false }))
  try {
    let response
    try {
      response = await getMedChronAssetByMatter(matterId)
    } catch (failure) {
      if (isCurrent()) dispatch(MedChronAssetActions.setAssetError({ matterId, hasError: true }))
      throw failure
    }
    if (!isCurrent()) return
    const { asset } = response
    dispatch(MedChronAssetActions.setAssetForMatter({ matterId, asset }))
    if (!asset || overviewOnly) return
    if (asset.active_update) dispatch(MedChronAssetActions.pollActiveUpdate({ assetId: asset.id, updateId: asset.active_update.id }))
    const state = getState().medChronAssetState
    if (force || state.loadedEventsVersionByAssetId[asset.id] !== asset.chronology_version || state.dataErrorByAssetId[asset.id]) {
      await loadMedChronAssetData(asset.id, dispatch, isCurrent)
    }
  } finally {
    dispatch(MedChronAssetActions.setAssetLoading({ matterId, isLoading: false }))
  }
}

/** Errors update the shared UI state and propagate so lease recovery cannot enable writes. */
export async function loadMedChronAssetData(assetId: string, dispatch: Dispatch, isCurrent = () => true) {
  if (!isCurrent()) return
  dispatch(MedChronAssetActions.setDataError({ assetId, hasError: false }))
  try {
    const [events, files] = await Promise.all([getMedChronAssetEvents(assetId), getMedChronAssetFiles(assetId)])
    if (!isCurrent()) return
    dispatch(MedChronEventsActions.setEventsList({ storeKey: assetId, events: events.events }))
    dispatch(MedChronReferencesActions.setReferencesRecord({ storeKey: assetId, references: events.references }))
    dispatch(MedChronAssetActions.setLoadedEventsVersion({ assetId, version: events.chronology_version }))
    dispatch(MedChronAssetActions.setFilesForAsset({ assetId, files: files.files }))
  } catch (failure) {
    if (isCurrent()) dispatch(MedChronAssetActions.setDataError({ assetId, hasError: true }))
    throw failure
  }
}
